/**
 * useTeamPermission hook
 *
 * Story 5.6: Team Member Management UI (Task 8)
 * Team-specific permission inference from current user's team membership.
 */

import { useMemo } from 'react';

import { useAuth } from '~/hooks/useAuth';
import { useTeamDetail } from '~/hooks/useTeams';

export interface TeamPermissions {
  /** Team admin can manage team members (add, remove, change roles) */
  canManageTeamMembers: boolean;
  /** User is a team admin */
  isTeamAdmin: boolean;
  /** Permissions are still loading */
  loading: boolean;
}

/**
 * Hook to check user permissions based on team role.
 * Derives permission from the team detail members array.
 *
 * @param teamId - Team ID to check permissions for
 * @returns Object with boolean flags for team permissions
 *
 * @example
 * const { canManageTeamMembers, isTeamAdmin } = useTeamPermission(teamId);
 * if (canManageTeamMembers) { showAddMemberButton(); }
 */
export function useTeamPermission(teamId: string | undefined): TeamPermissions {
  const { user } = useAuth();
  const { isLoading, team } = useTeamDetail(teamId);

  const permissions = useMemo((): TeamPermissions => {
    // Default to no permissions while loading
    if (isLoading || !team || !user) {
      return {
        canManageTeamMembers: false,
        isTeamAdmin: false,
        loading: isLoading,
      };
    }

    // Find current user's membership in the team
    const currentUserMembership = team.members?.find(m => m.user_id === user.uid);
    const isTeamAdmin = currentUserMembership?.role === 'team_admin';

    return {
      canManageTeamMembers: isTeamAdmin,
      isTeamAdmin,
      loading: false,
    };
  }, [team, user, isLoading]);

  return permissions;
}
